/* KrakenTheMeta dashboard — data layer.
 * Firestore, Scryfall and the Cloud Functions. The shapes and rules here are the ones the rest of
 * the site (deck-view, profiles, the Arena app, the functions) depend on, ported from the previous
 * dashboard (dashboard-folder/app.js) — cardShape, processGeneratedDeck, publishDeckAPI, the
 * import resolver, binders and the Arena publish flow keep their exact write shapes.
 */
import "../lib/cards.js";

(function () {
  "use strict";

  var KD = window.KD;
  var S = (KD.state = {
    user: null, profile: {}, isPro: false, isLegacyPatron: false, isPremium: false,
    decks: null, published: null, collection: null, binders: [], collectionLoaded: false
  });
  var D = (KD.data = KD.data || {}); // lib/cards.js already put the card + Scryfall helpers here
  var FN_BASE = "https://us-central1-krakenthemeta.cloudfunctions.net/";
  var BASICS = D.BASICS, chunked = D.chunked, huskCard = D.huskCard, SCRY_HEADERS = D.SCRY_HEADERS;

  function db() { return window.db || firebase.firestore(); }
  function FV() { return firebase.firestore.FieldValue; }

  D.userRef = function () { return db().collection("users").doc(S.user.id); };
  D.decksRef = function () { return D.userRef().collection("unpublishedDecks"); };
  D.deckRef = function (id) { return D.decksRef().doc(id); };
  D.cardsRef = function (id) { return D.deckRef(id).collection("cards"); };
  D.savedRef = function () { return D.userRef().collection("savedCards"); };
  D.bindersRef = function () { return D.userRef().collection("binders"); };
  D.pubMapRef = function () { return D.userRef().collection("publishedDecks"); };

  // ================================================================ plan + profile

  D.loadProfile = async function () {
    var snap = await D.userRef().get();
    S.profile = snap.exists ? (snap.data() || {}) : {};
    var p = S.profile;
    S.user.displayName = p.name || p.displayName || S.user.displayName || "Planeswalker";
    S.user.avatar = p.avatar || "/images/avatar1.png";
    var tier = p.patreon || p.patreonTier;
    var legacy = !!tier && ["tier1", "tier2", "tier3"].indexOf(tier) !== -1;
    var until = Number(p.assistant_pro_until || 0);
    S.isPro = p.assistant_plan === "pro" && (!until || Date.now() < until);
    S.isLegacyPatron = legacy && !S.isPro;
    S.isPremium = S.isPro || legacy; // same rule as isKrakenPaid() in functions/index.js
    return p;
  };

  // Mirrors ensureWeeklyGenerationLimit (functions/index.js): free = 1 build a week, a missing or
  // corrupt count reads as 1, and a 0 older than 7 days refills on the next build.
  D.gens = function () {
    if (S.isPremium) return { unlimited: true, left: Infinity, next: null };
    var p = S.profile || {}, left = p.generationsLeft;
    var last = KD.toDate(p.lastGenerationReset || p.generationsInitializedAt);
    if (left === undefined || left === null || left < 0) return { unlimited: false, left: 1, next: null };
    if (left === 0 && last && Date.now() - last.getTime() >= 7 * 864e5) return { unlimited: false, left: 1, next: null };
    return { unlimited: false, left: left, next: left === 0 && last ? new Date(last.getTime() + 7 * 864e5) : null };
  };
  D.nextResetText = function (g) {
    if (!g.next) return "";
    var days = Math.max(0, Math.ceil((g.next.getTime() - Date.now()) / 864e5));
    return days <= 1 ? "refills tomorrow" : "refills in " + days + " days";
  };

  D.saveProfile = async function (data) {
    await D.userRef().set(data, { merge: true });
    Object.assign(S.profile, data);
    S.user.displayName = data.name || S.user.displayName;
    if (data.avatar) S.user.avatar = data.avatar;
  };

  // POST to an HTTPS function with the user's ID token (checkout, billing, Patreon claim, deletion).
  D.callFn = async function (name, body) {
    var tok = await firebase.auth().currentUser.getIdToken();
    var r = await fetch(FN_BASE + name, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + tok },
      body: JSON.stringify(body || {})
    });
    var j = await r.json().catch(function () { return {}; });
    return { ok: r.ok, status: r.status, data: j || {} };
  };

  // ================================================================ decks

  D.loadDecks = async function (force) {
    if (S.decks && !force) return S.decks;
    // No orderBy: Firestore's orderBy silently drops docs missing the field (the oldest decks).
    var snap = await D.decksRef().get();
    S.decks = snap.docs.map(function (doc) {
      var data = doc.data() || {};
      var ms = (data.lastModified && data.lastModified.toMillis ? data.lastModified.toMillis() : 0) ||
               (data.createdAt && data.createdAt.toMillis ? data.createdAt.toMillis() : 0);
      // husk: a shell a refused generation left behind — 0 cards, no art, never renamed, 24h+ old
      var husk = typeof data.cardCount === "number" && data.cardCount === 0 && !data.headerImage && !data.renamedAt && ms > 0 && (Date.now() - ms) > 864e5;
      return { id: doc.id, name: data.name || "Untitled deck", format: data.format || "", cardCount: typeof data.cardCount === "number" ? data.cardCount : null,
        headerImage: data.headerImage || null, nameAuto: !!data.nameAuto, _sort: ms, _husk: husk };
    }).sort(function (a, b) { return b._sort - a._sort; });
    D.sweepHusks();
    return S.decks;
  };

  D.loadPublished = async function (force) {
    if (S.published && !force) return S.published;
    var map = {};
    try { var snap = await D.pubMapRef().get(); snap.forEach(function (d) { map[d.id] = (d.data() || {}).publicDeckId || null; }); }
    catch (e) { console.warn("published map", e); }
    S.published = map;
    return map;
  };

  var sweeping = false;
  D.sweepHusks = async function () {
    if (sweeping || !S.decks) return;
    sweeping = true;
    try {
      var open = KD.builder && KD.builder.deckId;
      var candidates = S.decks.filter(function (d) { return d._husk && d.id !== open; }).slice(0, 10);
      var gone = new Set();
      await Promise.all(candidates.map(async function (d) {
        try {
          var cs = await D.cardsRef(d.id).limit(1).get();
          if (!cs.empty) { d._husk = false; return; } // any card → never delete
          await D.deckRef(d.id).delete();
          gone.add(d.id);
        } catch (e) { console.warn("husk sweep", d.id, e); }
      }));
      if (gone.size) {
        S.decks = S.decks.filter(function (d) { return !gone.has(d.id); });
        document.dispatchEvent(new CustomEvent("kd:decks"));
      }
    } finally { sweeping = false; }
  };

  // Fill missing card counts / header art in parallel and persist them for next time.
  D.hydrateDeckCounts = async function () {
    var need = (S.decks || []).filter(function (d) { return d.cardCount == null || (d.cardCount > 0 && !d.headerImage); });
    if (!need.length) return false;
    await Promise.all(need.map(async function (d) {
      try {
        var cs = await D.cardsRef(d.id).get();
        var datas = cs.docs.map(function (x) { return x.data(); });
        d.cardCount = datas.reduce(function (s, c) { return s + (parseInt(c.quantity, 10) || 0); }, 0);
        var hdr = D.pickHeaderCard(datas);
        if (hdr) d.headerImage = hdr;
        var upd = { cardCount: d.cardCount };
        if (hdr) upd.headerImage = hdr;
        D.deckRef(d.id).update(upd).catch(function () {});
      } catch (e) { d.cardCount = d.cardCount == null ? 0 : d.cardCount; }
    }));
    return true;
  };

  // auto: the name is a placeholder; once the AI build is done the Kraken names the deck (D.nameNewDeck)
  D.createDeck = async function (name, format, auto) {
    var doc = { name: name, format: format, cardCount: 0, createdAt: FV().serverTimestamp(), lastModified: FV().serverTimestamp() };
    if (auto) doc.nameAuto = true;
    var ref = await D.decksRef().add(doc);
    var deck = { id: ref.id, name: name, format: format, cardCount: 0, headerImage: null, nameAuto: !!auto, _sort: Date.now(), _husk: false };
    if (S.decks) S.decks.unshift(deck);
    return deck;
  };

  D.fetchDeck = async function (id) {
    var cached = (S.decks || []).find(function (d) { return d.id === id; });
    if (cached) return cached;
    try {
      var s = await D.deckRef(id).get();
      if (!s.exists) return null;
      var data = s.data() || {};
      return { id: s.id, name: data.name || "Untitled deck", format: data.format || "", cardCount: typeof data.cardCount === "number" ? data.cardCount : null, headerImage: data.headerImage || null, nameAuto: !!data.nameAuto };
    } catch (e) { return null; }
  };

  D.getDeckCards = async function (deckId) {
    var snap = await D.cardsRef(deckId).get();
    if (snap.size) return snap.docs.map(function (d) { return Object.assign({ id: d.id }, d.data()); });
    // very old decks stored cards inline on the deck doc
    try {
      var doc = await D.deckRef(deckId).get();
      var inline = doc.exists && Array.isArray(doc.data().cards) ? doc.data().cards : [];
      return inline.map(function (c, i) { return Object.assign({ id: c.id || ("inline-" + i) }, c); });
    } catch (e) { return []; }
  };

  // Self-heal: cards saved without data (an old network/adblock failure wrote husks) are
  // re-resolved from Scryfall and patched in place. Returns how many were repaired.
  D.healDeckCards = async function (deckId, cards) {
    var broken = cards.filter(function (c) { return c.id && String(c.id).indexOf("inline-") !== 0 && !c.imageUrl && c.name; });
    if (broken.length < 3) return 0;
    var names = Array.from(new Set(broken.map(function (c) { return c.name; })));
    var byName = new Map();
    var groups = chunked(names, 70);
    for (var i = 0; i < groups.length; i++) {
      try {
        var res = await D.scryfall("https://api.scryfall.com/cards/collection", {
          method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ identifiers: groups[i].map(function (n) { return { name: n }; }) })
        });
        if (res.ok) { var j = await res.json(); (j.data || []).forEach(function (cd) { if (cd && cd.name) byName.set(cd.name.toLowerCase(), cd); }); }
      } catch (e) { /* offline */ }
      if (i < groups.length - 1) await KD.delay(120);
    }
    if (!byName.size) return 0;
    var batch = db().batch(), ops = 0, healed = 0;
    for (var k = 0; k < broken.length; k++) {
      var c = broken[k];
      var cd = byName.get(String(c.name).toLowerCase());
      if (!cd) continue;
      var fresh = D.cardShape(cd, c.quantity || 1, !!c.isCommander);
      delete fresh.quantity;
      batch.update(D.cardsRef(deckId).doc(c.id), fresh);
      Object.assign(c, fresh);
      healed++; ops++;
      if (ops >= 400) { await batch.commit(); batch = db().batch(); ops = 0; }
    }
    if (ops) await batch.commit();
    return healed;
  };

  D.setDeckName = async function (deckId, name) {
    // renamedAt marks deliberate naming — the husk sweep never touches a renamed deck
    await D.deckRef(deckId).update({ name: name, nameAuto: FV().delete(), renamedAt: FV().serverTimestamp(), lastModified: FV().serverTimestamp() });
    var d = (S.decks || []).find(function (x) { return x.id === deckId; });
    if (d) { d.name = name; d.nameAuto = false; }
  };
  D.touchDeck = function (deckId) { return D.deckRef(deckId).update({ lastModified: FV().serverTimestamp() }).catch(function () {}); };
  D.saveDeckMeta = function (deckId, meta) {
    var d = (S.decks || []).find(function (x) { return x.id === deckId; });
    if (d) Object.assign(d, meta);
    return D.deckRef(deckId).update(meta).catch(function () {});
  };

  D.setQty = async function (deckId, card, qty) {
    var ref = D.cardsRef(deckId).doc(card.id);
    if (qty <= 0) await ref.delete(); else await ref.update({ quantity: qty });
    card.quantity = qty;
  };
  D.addToDeck = async function (deckId, cards, scryCard) {
    var existing = cards.find(function (c) { return c.name === scryCard.name; });
    if (existing) {
      var q = (parseInt(existing.quantity, 10) || 1) + 1;
      await D.cardsRef(deckId).doc(existing.id).update({ quantity: q });
      existing.quantity = q;
      return existing;
    }
    var shape = D.toShape(scryCard, 1, false);
    var doc = await D.cardsRef(deckId).add(shape);
    var card = Object.assign({ id: doc.id }, shape);
    cards.push(card);
    return card;
  };
  // One commander (or a legal partner pair) at the helm: flip the flag on this card.
  D.setCommander = async function (deckId, card, on) {
    await D.cardsRef(deckId).doc(card.id).update({ isCommander: !!on });
    card.isCommander = !!on;
  };
  // Add a pasted list to a deck. Lines naming the same card merge, and a card the deck already
  // runs gets its count raised instead of a second row.
  // 2026-10-03: a card under a "Commander" header (or tagged *CMDR* / [Commander]) is written as the
  // commander — imports used to lose it — and sideboard/maybeboard lines stay out of the deck.
  D.importToDeck = async function (deckId, parsed, resolved, existingCards) {
    var unmatchedLines = [], sideLines = [], matchedLines = 0, importedQty = 0, byCanon = new Map();
    parsed.forEach(function (p) {
      if (p.side) { sideLines.push(p.line); return; }
      var cd = resolved.get(p.name.toLowerCase());
      if (!cd) { unmatchedLines.push(p.line); return; }
      matchedLines++; importedQty += p.qty;
      var key = cd.name.toLowerCase(), cur = byCanon.get(key);
      if (cur) { cur.qty += p.qty; cur.commander = cur.commander || !!p.commander; }
      else byCanon.set(key, { cd: cd, qty: p.qty, commander: !!p.commander });
    });
    var have = new Map((existingCards || []).map(function (c) { return [String(c.name || "").toLowerCase(), c]; }));
    var batch = db().batch(), ops = 0;
    var entries = Array.from(byCanon.values());
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i], ex = have.get(e.cd.name.toLowerCase());
      if (ex && ex.id && String(ex.id).indexOf("inline-") !== 0) {
        var upd = { quantity: (parseInt(ex.quantity, 10) || 1) + e.qty };
        if (e.commander) upd.isCommander = true;
        batch.update(D.cardsRef(deckId).doc(ex.id), upd);
      } else batch.set(D.cardsRef(deckId).doc(), D.cardShape(e.cd, e.qty, e.commander));
      if (++ops >= 400) { await batch.commit(); batch = db().batch(); ops = 0; }
    }
    if (ops) await batch.commit();
    return { matchedLines: matchedLines, importedQty: importedQty, unmatchedLines: unmatchedLines, sideLines: sideLines,
      commanders: entries.filter(function (x) { return x.commander; }).map(function (x) { return x.cd.name; }) };
  };

  // A commander list with no marked commander: the legend (one copy) whose colors cover every other
  // card's — the smallest such identity wins, so "Krenko" beats a five-color legend in a mono-red deck.
  // Returns the card or null when nothing fits (then nothing is guessed).
  D.guessCommander = function (cards, format) {
    var colors = new Set();
    cards.forEach(function (c) {
      if (/land/i.test(c.types || "") && !/creature/i.test(c.types || "")) return;
      (c.colorIdentity || []).forEach(function (x) { colors.add(x); });
    });
    var best = null;
    cards.forEach(function (c) {
      if ((parseInt(c.quantity, 10) || 1) !== 1 || !D.canBeCommander(c, format)) return;
      var ci = c.colorIdentity || [];
      var covers = Array.from(colors).every(function (x) { return ci.indexOf(x) !== -1; });
      if (covers && (!best || ci.length < (best.colorIdentity || []).length)) best = c;
    });
    return best;
  };

  // ---- publishing (same collections and write shape as before)
  async function syncPublicCards(unpubRef, pubRef, clearFirst, snap) {
    if (clearFirst) {
      var old = await pubRef.collection("cards").get();
      await Promise.all(old.docs.map(function (d) { return pubRef.collection("cards").doc(d.id).delete(); }));
    }
    snap = snap || await unpubRef.collection("cards").get();
    await Promise.all(snap.docs.map(function (d) { return pubRef.collection("cards").doc(d.id).set(d.data()); }));
  }
  D.isDeckPublished = async function (deckId) {
    try { var d = await D.pubMapRef().doc(deckId).get(); return d.exists; } catch (e) { return false; }
  };
  D.publishDeck = async function (deckId) {
    var userId = S.user.id;
    var unpub = D.deckRef(deckId);
    var pubCol = db().collection("publicDecks");
    var userDoc = await D.userRef().get();
    var displayName = (userDoc.data() || {}).name || "Unknown User";
    var deckDoc = await unpub.get();
    if (!deckDoc.exists) throw new Error("Deck not found");
    var deckData = deckDoc.data();
    deckData.displayName = displayName;
    deckData.publisherId = userId;
    // the real total (the library's count can lag a fresh build): the server waits for exactly this
    // many cards to land before it reads the commander off the public copy
    var cardsSnap = await unpub.collection("cards").get();
    deckData.cardCount = cardsSnap.docs.reduce(function (s, d) { return s + (parseInt(d.data().quantity, 10) || 1); }, 0);
    var now = FV().serverTimestamp();
    var mapping = await D.pubMapRef().doc(deckId).get();
    var publicDeckId = mapping.exists ? mapping.data().publicDeckId : null;
    var existing = publicDeckId ? await pubCol.doc(publicDeckId).get() : null;
    if (existing && existing.exists) {
      var e = existing.data();
      deckData.views = e.views || 0;
      deckData.likes = e.likes || 0;
      deckData.updatedAt = now;
      await pubCol.doc(publicDeckId).update(deckData);
      await syncPublicCards(unpub, pubCol.doc(publicDeckId), true, cardsSnap);
      await D.pubMapRef().doc(deckId).update({ updatedAt: now });
    } else {
      // first publish — or the public copy was removed, in which case publish a fresh one
      deckData.createdAt = now; deckData.updatedAt = now; deckData.views = 0; deckData.likes = 0;
      var newRef = await pubCol.add(deckData);
      await syncPublicCards(unpub, newRef, false, cardsSnap);
      await D.pubMapRef().doc(deckId).set({ publicDeckId: newRef.id, name: deckData.name, format: deckData.format, createdAt: now, updatedAt: now, displayName: displayName });
      publicDeckId = newRef.id;
    }
    if (S.published) S.published[deckId] = publicDeckId;
    return publicDeckId;
  };
  D.unpublishDeck = async function (deckId) {
    var mapRef = D.pubMapRef().doc(deckId);
    var mapSnap = await mapRef.get();
    if (!mapSnap.exists) return;
    var publicId = (mapSnap.data() || {}).publicDeckId;
    if (publicId) {
      var pubRef = db().collection("publicDecks").doc(publicId);
      var cs = await pubRef.collection("cards").get();
      var groups = chunked(cs.docs, 400);
      for (var i = 0; i < groups.length; i++) {
        var b = db().batch();
        groups[i].forEach(function (d) { b.delete(d.ref); });
        await b.commit();
      }
      await pubRef.delete();
    }
    await mapRef.delete();
    if (S.published) delete S.published[deckId];
  };
  D.deleteDeck = async function (deckId) {
    try { await D.unpublishDeck(deckId); } catch (e) { console.error("unpublish before delete", e); }
    var cards = await D.cardsRef(deckId).get();
    var groups = chunked(cards.docs, 400);
    for (var i = 0; i < groups.length; i++) {
      var b = db().batch();
      groups[i].forEach(function (c) { b.delete(c.ref); });
      await b.commit();
    }
    await D.deckRef(deckId).delete();
    if (S.decks) S.decks = S.decks.filter(function (d) { return d.id !== deckId; });
  };

  // ================================================================ AI

  // Same callable and payload as before, so the pipeline sees exactly what it always has.
  D.generate = async function (deck, prompt, o) {
    var fn = firebase.functions().httpsCallable("generateDeckWithWebSearch", { timeout: 540000 });
    var resp = await fn({
      userQuery: prompt, format: deck.format,
      useCollection: !!o.useCollection, casual: !!o.casual, mode: "replace", existingCards: [],
      budget: o.budget || null, bracket: o.bracket || null, commander: o.commander || null,
      binderId: o.binderId || null, exclude: o.exclude || [], include: o.include || [], reserved: o.reserved || null
    });
    return resp.data || {};
  };

  // Writes the server's decklist into the deck. Batch-resolves card data (70 names per request)
  // and NEVER drops a card: a name the lookup misses is still written, so 100 stays 100.
  // serverCards (2026-10-05): the card data the generator now sends with the deck, keyed by the name as
  // written — those cards need no Scryfall lookup at all; only names it lacks are looked up here.
  D.processGeneratedDeck = async function (deck, deckHtml, commanderName, mode, partnerName, serverCards) {
    mode = mode || "replace";
    var lines = String(deckHtml || "").replace(/<br\s*\/?>/gi, "\n").split("\n");
    var deckCardsRef = D.cardsRef(deck.id);
    var existingNonBasicNames = new Set();
    var existingCommanderPresent = false;
    var existing = await deckCardsRef.get();
    if (mode !== "finish") {
      var groupsDel = chunked(existing.docs, 400);
      for (var gd = 0; gd < groupsDel.length; gd++) {
        var bd = db().batch();
        groupsDel[gd].forEach(function (doc) { bd.delete(doc.ref); });
        await bd.commit();
      }
    } else {
      existing.forEach(function (doc) {
        var d = doc.data() || {};
        if (d.isCommander) existingCommanderPresent = true;
        if (d.name && !BASICS.has(d.name.toLowerCase())) existingNonBasicNames.add(d.name.toLowerCase());
      });
    }

    var parsed = [];
    lines.forEach(function (line) {
      line = line.trim();
      if (!line || line.indexOf("Total Cards:") !== -1) return;
      var marked = line.indexOf("[COMMANDER]") !== -1;
      line = line.replace(/\s*\[COMMANDER\]/, "");
      var parts = line.split(/\s+/);
      var quantity = parseInt(parts[0], 10) || 1;
      var name = parts.slice(1).join(" ");
      if (name) parsed.push({ name: name, quantity: quantity, marked: marked });
    });

    var dataByName = new Map();
    if (serverCards && typeof serverCards === "object") {
      Object.keys(serverCards).forEach(function (k) {
        var cd = serverCards[k];
        if (cd && cd.name) dataByName.set(String(k).trim().split(/\s+/).join(" ").toLowerCase(), cd);
      });
    }
    var uniqueNames = Array.from(new Set(parsed.map(function (p) { return p.name; })));
    var groups = chunked(uniqueNames.filter(function (n) { return !dataByName.has(n.toLowerCase()); }), 70);
    for (var gi = 0; gi < groups.length; gi++) {
      for (var attempt = 1; attempt <= 3; attempt++) {
        try {
          var res = await D.scryfall("https://api.scryfall.com/cards/collection", {
            method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ identifiers: groups[gi].map(function (n) { return { name: n }; }) })
          });
          if (res.ok) { var j = await res.json(); (j.data || []).forEach(function (cd) { if (cd && cd.name) dataByName.set(cd.name.toLowerCase(), cd); }); break; }
        } catch (e) { /* retry */ }
        if (attempt < 3) await KD.delay(600 * attempt);
      }
      if (gi < groups.length - 1) await KD.delay(120);
    }
    for (var pi = 0; pi < parsed.length; pi++) {
      var p = parsed[pi];
      if (dataByName.has(p.name.toLowerCase())) continue;
      try {
        var r = await D.scryfall("https://api.scryfall.com/cards/named?fuzzy=" + encodeURIComponent(p.name.split("//")[0].trim()), { headers: SCRY_HEADERS });
        if (r.ok) { var cdf = await r.json(); if (cdf && cdf.name) dataByName.set(p.name.toLowerCase(), cdf); }
      } catch (e) { /* written below regardless */ }
      await KD.delay(90);
    }
    var resolvedCount = uniqueNames.filter(function (n) { return dataByName.has(n.toLowerCase()); }).length;

    var commanderSet = existingCommanderPresent, partnerSet = false;
    var isCmd = D.isCommanderFormat(deck.format);
    var writeBatch = db().batch(), ops = 0;
    for (var wi = 0; wi < parsed.length; wi++) {
      var q = parsed[wi];
      var cd = dataByName.get(q.name.toLowerCase());
      var resolvedName = cd ? cd.name : q.name;
      if (mode === "finish" && isCmd && existingNonBasicNames.has(resolvedName.toLowerCase())) continue;
      var shouldBeCommander = false;
      if (isCmd && !commanderSet) {
        if (q.marked || (commanderName && D.namesMatchLoose(resolvedName, commanderName)) ||
            (!commanderName && q.quantity === 1 && cd && D.canBeCommander({ type: cd.type_line, oracleText: cd.oracle_text }, deck.format))) {
          shouldBeCommander = true; commanderSet = true;
        }
      }
      if (isCmd && partnerName && !partnerSet && !shouldBeCommander && D.namesMatchLoose(resolvedName, partnerName)) {
        shouldBeCommander = true; partnerSet = true;
      }
      var card = cd ? D.cardShape(cd, q.quantity, shouldBeCommander) : huskCard(resolvedName, q.quantity, shouldBeCommander);
      writeBatch.set(deckCardsRef.doc(), card);
      if (++ops >= 400) { await writeBatch.commit(); writeBatch = db().batch(); ops = 0; }
    }
    if (ops) await writeBatch.commit();
    if (isCmd && !commanderSet) await D.autoSelectCommander(deck.id);
    return { resolved: resolvedCount, unique: uniqueNames.length };
  };

  D.autoSelectCommander = async function (deckId) {
    try {
      var snap = await D.cardsRef(deckId).get();
      var hit = snap.docs.find(function (doc) { var c = doc.data(); return D.canBeCommander(c) && c.quantity === 1; });
      if (hit) await hit.ref.update({ isCommander: true });
    } catch (e) { console.error("autoSelectCommander", e); }
  };

  // extra: { commander, format, theme } — context that makes the name fit (theme = what was asked for)
  D.aiName = async function (cards, extra, timeout) {
    var fn = firebase.functions().httpsCallable("generateDeckName", { timeout: timeout || 120000 });
    var cardList = cards.map(function (c) { return c.quantity > 1 ? c.quantity + "x " + c.name : c.name; }).join(", ");
    var resp = await fn(Object.assign({ cardList: cardList }, extra || {}));
    var raw = D.cleanDeckName(resp.data && resp.data.deckName);
    return raw || null;
  };
  D.cleanDeckName = function (s) {
    s = String(s || "").split("\n").map(function (l) { return l.trim(); }).filter(Boolean)[0] || "";
    s = s.replace(/^(deck\s*name|name)\s*[:\-–—]\s*/i, "").replace(/^["'“”‘’*_`#]+|["'“”‘’*_`.!]+$/g, "").replace(/\s+/g, " ").trim();
    if (s.length > 60) s = s.slice(0, 60).replace(/\s+\S*$/, "");
    return s;
  };

  // A deck the AI just built for someone who didn't name it: the Kraken names it from the list, the
  // commander and what they asked for. If that fails it's named after the commander, never the prompt.
  D.nameNewDeck = async function (deck, info) {
    var cards = D.suggestionCards(info.suggestion), name = null;
    var commander = [info.commander, info.partner].filter(Boolean).join(" + ");
    try {
      if (cards.length) name = await D.aiName(cards, { commander: commander || null, format: D.formatLabel(deck.format), theme: String(info.prompt || "").slice(0, 400) }, 30000);
    } catch (e) { console.warn("deck name", e); }
    if (!name || name.toLowerCase() === String(info.prompt || "").trim().toLowerCase()) name = D.fallbackDeckName(deck, info.commander, info.partner);
    await D.deckRef(deck.id).update({ name: name, nameAuto: FV().delete() });
    deck.name = name;
    deck.nameAuto = false;
    var d = (S.decks || []).find(function (x) { return x.id === deck.id; });
    if (d) { d.name = name; d.nameAuto = false; }
    return name;
  };
  D.fallbackDeckName = function (deck, commander, partner) {
    var short = function (n) { return String(n).split(",")[0].trim(); };
    if (commander && partner) return short(commander) + " & " + short(partner);
    if (commander) return String(commander).slice(0, 60);
    return D.formatLabel(deck.format) + " brew";
  };
  // "1 Sol Ring<br>1 Krenko, Mob Boss [COMMANDER]…" → [{ name, quantity }]
  D.suggestionCards = function (text) {
    var out = [];
    String(text || "").replace(/<br\s*\/?>/gi, "\n").split("\n").forEach(function (line) {
      var m = line.trim().replace(/\s*\[COMMANDER\]/, "").match(/^(\d+)\s+(.+)$/);
      if (m) out.push({ name: m[2].trim(), quantity: parseInt(m[1], 10) || 1 });
    });
    return out;
  };

  // ================================================================ improve a deck (upgradeDeck)
  // Keeps the deck and its commander; the server proposes swaps checked against Scryfall, the
  // format, the colors, the bracket and the land count (functions/deck-upgrade.js).
  D.upgrade = async function (deck, cards, o) {
    o = o || {};
    var fn = firebase.functions().httpsCallable("upgradeDeck", { timeout: 300000 });
    var resp = await fn({
      format: deck.format,
      cards: cards.filter(function (c) { return c.name; }).map(function (c) { return { name: c.name, quantity: parseInt(c.quantity, 10) || 1, isCommander: !!c.isCommander }; }),
      goal: o.goal || "", changes: o.changes || null, bracket: o.bracket || null, budgetPerCard: o.budgetPerCard || null,
      useCollection: !!o.useCollection, binderId: o.binderId || null, reserved: o.reserved || null,
      keep: o.keep || [], exclude: o.exclude || [], avoid: o.avoid || [], casual: !!o.casual
    });
    return resp.data || {};
  };

  // Write the kept swaps: cuts lower or delete rows, adds raise a row or write a new card.
  // picks = { cuts: [{name, qty}], adds: [{name, qty}] } → { cut, add, missing: [names] }
  D.applyUpgrade = async function (deckId, cards, picks) {
    var lc = function (s) { return String(s || "").toLowerCase(); };
    var front = function (s) { return lc(s).split(" // ")[0]; };
    var rowFor = function (name) {
      return cards.find(function (c) { return lc(c.name) === lc(name); }) || cards.find(function (c) { return front(c.name) === front(name); });
    };
    var adds = (picks.adds || []).filter(function (a) { return a && a.name && a.qty > 0; });
    // card data the upgrade engine sent (2026-10-05); only cards it lacks are looked up on Scryfall
    var resolved = new Map();
    var given = picks.cardData && typeof picks.cardData === "object" ? picks.cardData : {};
    adds.forEach(function (a) { var cd = given[a.name]; if (cd && cd.name) resolved.set(lc(a.name), cd); });
    var unknown = adds.filter(function (a) { return !resolved.has(lc(a.name)); });
    if (unknown.length) (await D.resolveCardNames(unknown.map(function (a) { return a.name; }))).resolved.forEach(function (cd, k) { resolved.set(k, cd); });
    var batch = db().batch(), ops = 0, out = { cut: 0, add: 0, missing: [] };
    var left = new Map(); // row id -> quantity after this apply (a cut-to-zero row is gone)
    var commit = async function () { if (++ops >= 400) { await batch.commit(); batch = db().batch(); ops = 0; } };
    for (var i = 0; i < (picks.cuts || []).length; i++) {
      var c = picks.cuts[i], row = rowFor(c.name);
      if (!row || !row.id || String(row.id).indexOf("inline-") === 0) { out.missing.push(c.name); continue; }
      var have = left.has(row.id) ? left.get(row.id) : (parseInt(row.quantity, 10) || 1);
      var next = have - c.qty;
      left.set(row.id, Math.max(0, next));
      out.cut += Math.min(c.qty, have);
    }
    for (var j = 0; j < adds.length; j++) {
      var a = adds[j], cd = resolved.get(lc(a.name)), r2 = rowFor(cd ? cd.name : a.name);
      if (r2 && r2.id && String(r2.id).indexOf("inline-") !== 0 && (!left.has(r2.id) || left.get(r2.id) > 0)) {
        var base = left.has(r2.id) ? left.get(r2.id) : (parseInt(r2.quantity, 10) || 1);
        left.set(r2.id, base + a.qty);
      } else {
        batch.set(D.cardsRef(deckId).doc(), cd ? D.cardShape(cd, a.qty, false) : huskCard(a.name, a.qty, false));
        await commit();
      }
      out.add += a.qty;
    }
    for (var e of left.entries()) {
      var ref = D.cardsRef(deckId).doc(e[0]);
      if (e[1] <= 0) batch.delete(ref); else batch.update(ref, { quantity: e[1] });
      await commit();
    }
    if (ops) await batch.commit();
    return out;
  };

  // Copies of each card held by the user's OTHER decks (for "don't reuse my cards"). Basics exempt.
  // 2026-10-03: the cache expires after two minutes (it used to live for the whole session, so a deck
  // edited in between was still counted with its old cards).
  var reservedCache = null;
  D.computeReserved = async function (deckId) {
    if (reservedCache && reservedCache.deckId === deckId && Date.now() - reservedCache.at < 120000) return reservedCache;
    var snap = await D.decksRef().get();
    var others = snap.docs.filter(function (d) { return d.id !== deckId; });
    var map = {}, deckCount = 0;
    for (var i = 0; i < others.length; i++) {
      var cs = await others[i].ref.collection("cards").get();
      var any = false;
      cs.forEach(function (c) {
        var cd = c.data(), n = (cd.name || "").toLowerCase();
        if (!n || BASICS.has(n)) return;
        map[n] = (map[n] || 0) + (cd.quantity || 1); any = true;
      });
      if (any) deckCount++;
    }
    reservedCache = { deckId: deckId, map: map, deckCount: deckCount, at: Date.now() };
    return reservedCache;
  };

  D.basicLandFraction = function (deckText) {
    var total = 0, basics = 0;
    String(deckText || "").replace(/<br\s*\/?>/gi, "\n").split("\n").forEach(function (line) {
      var m = line.trim().replace(/\s*\[COMMANDER\]/, "").match(/^(\d+)\s+(.+)$/);
      if (!m) return;
      var q = parseInt(m[1], 10) || 0;
      total += q;
      if (BASICS.has(m[2].trim().toLowerCase())) basics += q;
    });
    return total > 0 ? basics / total : 0;
  };

  // Someone asking the GENERATOR to critique a deck they own: suggest the analyzer instead.
  // Every pattern needs an explicit feedback phrase; "improve" alone is ordinary build language.
  D.reviewIntent = function (text) {
    var q = String(text || "").toLowerCase();
    if (!q) return false;
    return [
      /\broast\b/, /\b(rate|critique|criticis|criticiz)\w*\b[\s\S]{0,20}\bdeck\b/,
      /\b(review|look over|look at)\b[\s\S]{0,12}\b(my|this|the)\b[\s\S]{0,12}\bdeck\b/,
      /\bimprove\b[\s\S]{0,12}\b(my|this|the)\b[\s\S]{0,12}\bdeck\b/, /\bhow (can|do|should) i improve\b/,
      /\bfix\b[\s\S]{0,12}\b(my|this|the)\b[\s\S]{0,12}\bdeck\b/, /\bwhat should i cut\b/, /\bwhat'?s wrong with (my|this)\b/,
      /\bhere'?s (my|the) (list|decklist)\b/, /\bhere is (my|the) (list|decklist)\b/, /\b(i'?m|im) having issues with\b/,
      /\bcopied (the|my) deck\b/, /\bthe deck is called\b/, /\bfind (any )?(better )?(alternatives|replacements)\b/
    ].some(function (re) { return re.test(q); });
  };

  // ================================================================ collection

  D.loadCollection = async function (force) {
    if (S.collection && !force) return S.collection;
    // cards and binders load independently: a binders failure must never blank the collection
    try {
      var cs = await D.savedRef().get();
      S.collection = cs.docs.map(function (d) { return Object.assign({ id: d.id }, d.data()); });
      S.collectionLoaded = true;
    } catch (e) { console.error("loadCollection", e); S.collection = []; S.collectionLoaded = false; }
    try {
      var bs = await D.bindersRef().get();
      S.binders = bs.docs.map(function (d) { return Object.assign({ id: d.id }, d.data()); }).sort(function (a, b) { return (a.name || "").localeCompare(b.name || ""); });
    } catch (e) { console.error("loadBinders", e); S.binders = []; }
    return S.collection;
  };
  D.collectionTotals = function (list) {
    list = list || S.collection || [];
    return {
      cards: list.reduce(function (t, c) { return t + (c.quantity || 0); }, 0),
      unique: list.length,
      value: list.reduce(function (t, c) { return t + (parseFloat(c.price) || 0) * (c.quantity || 0); }, 0)
    };
  };
  D.saveToCollection = async function (scryCard, qty) {
    qty = qty || 1;
    await D.loadCollection();
    var existing = S.collection.find(function (c) { return c.name === scryCard.name; });
    if (existing) {
      var n = (existing.quantity || 1) + qty;
      await D.savedRef().doc(existing.id).update({ quantity: n });
      existing.quantity = n;
      return existing;
    }
    var shape = D.toShape(scryCard, qty, false);
    var doc = await D.savedRef().add(shape);
    var card = Object.assign({ id: doc.id }, shape);
    S.collection.push(card);
    return card;
  };
  D.setCollectionQty = async function (card, qty) {
    if (qty <= 0) {
      await D.savedRef().doc(card.id).delete();
      S.collection = S.collection.filter(function (c) { return c.id !== card.id; });
    } else {
      await D.savedRef().doc(card.id).update({ quantity: qty });
      card.quantity = qty;
    }
  };
  D.clearCollection = async function (onProgress) {
    var snap = await D.savedRef().get();
    var docs = snap.docs;
    var totalQty = docs.reduce(function (t, d) { return t + (d.data().quantity || 0); }, 0);
    var groups = chunked(docs, 400);
    for (var i = 0; i < groups.length; i++) {
      var b = db().batch();
      groups[i].forEach(function (d) { b.delete(d.ref); });
      await b.commit();
      if (onProgress) onProgress(Math.min((i + 1) * 400, docs.length), docs.length);
    }
    S.collection = [];
    return { totalQty: totalQty, unique: docs.length };
  };
  // Merge lines by canonical card name, then batch the writes (400 per commit).
  D.importCollection = async function (parsed, resolved) {
    await D.loadCollection();
    var byCanon = new Map(), unmatchedLines = [], matchedLines = 0, importedQty = 0;
    parsed.forEach(function (p) {
      var cd = resolved.get(p.name.toLowerCase());
      if (!cd) { unmatchedLines.push(p.line); return; }
      matchedLines++; importedQty += p.qty;
      var key = cd.name.toLowerCase(), cur = byCanon.get(key);
      if (cur) cur.qty += p.qty; else byCanon.set(key, { cd: cd, qty: p.qty });
    });
    var existingByName = new Map(S.collection.map(function (c) { return [(c.name || "").toLowerCase(), c]; }));
    var batch = db().batch(), ops = 0, fresh = [];
    var entries = Array.from(byCanon.values());
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i], ex = existingByName.get(e.cd.name.toLowerCase());
      if (ex) { var nq = (ex.quantity || 0) + e.qty; batch.update(D.savedRef().doc(ex.id), { quantity: nq }); ex.quantity = nq; }
      else { var shape = D.cardShape(e.cd, e.qty, false), ref = D.savedRef().doc(); batch.set(ref, shape); fresh.push(Object.assign({ id: ref.id }, shape)); }
      if (++ops >= 400) { await batch.commit(); batch = db().batch(); ops = 0; }
    }
    if (ops) await batch.commit();
    S.collection.push.apply(S.collection, fresh);
    return { matchedLines: matchedLines, importedQty: importedQty, unmatchedLines: unmatchedLines };
  };

  // Binders: users/{uid}/binders/{id} = {name, createdAt}; membership lives on the card as binderIds[].
  D.createBinder = async function (name) {
    name = String(name || "").trim();
    if (!name) throw new Error("Enter a binder name");
    if (S.binders.some(function (b) { return (b.name || "").toLowerCase() === name.toLowerCase(); })) throw new Error("A binder with that name already exists");
    var ref = await D.bindersRef().add({ name: name, createdAt: FV().serverTimestamp() });
    S.binders.push({ id: ref.id, name: name });
    S.binders.sort(function (a, b) { return (a.name || "").localeCompare(b.name || ""); });
    return ref.id;
  };
  D.renameBinder = async function (id, name) {
    name = String(name || "").trim();
    var b = S.binders.find(function (x) { return x.id === id; });
    if (!b || !name || name === b.name) return;
    if (S.binders.some(function (x) { return x.id !== id && (x.name || "").toLowerCase() === name.toLowerCase(); })) throw new Error("A binder with that name already exists");
    await D.bindersRef().doc(id).update({ name: name });
    b.name = name;
    S.binders.sort(function (a, c) { return (a.name || "").localeCompare(c.name || ""); });
  };
  D.deleteBinder = async function (id) {
    var affected = (S.collection || []).filter(function (c) { return (c.binderIds || []).indexOf(id) !== -1; });
    var groups = chunked(affected, 400);
    for (var i = 0; i < groups.length; i++) {
      var b = db().batch();
      groups[i].forEach(function (c) { b.update(D.savedRef().doc(c.id), { binderIds: FV().arrayRemove(id) }); });
      await b.commit();
    }
    affected.forEach(function (c) { c.binderIds = (c.binderIds || []).filter(function (x) { return x !== id; }); });
    await D.bindersRef().doc(id).delete();
    S.binders = S.binders.filter(function (x) { return x.id !== id; });
  };
  D.toggleCardBinder = async function (card, binderId, on) {
    await D.savedRef().doc(card.id).update({ binderIds: on ? FV().arrayUnion(binderId) : FV().arrayRemove(binderId) });
    var set = new Set(card.binderIds || []);
    if (on) set.add(binderId); else set.delete(binderId);
    card.binderIds = Array.from(set);
  };

  // ================================================================ Arena

  D.loadArena = async function () {
    var res = await Promise.all([
      D.userRef().collection("assistantMatches").orderBy("endedAt", "desc").limit(50).get(),
      D.userRef().collection("assistantDecks").orderBy("lastPlayedAt", "desc").limit(25).get().catch(function () { return null; })
    ]);
    return {
      matches: res[0].docs.map(function (d) { return Object.assign({ _id: d.id }, d.data()); }),
      decks: res[1] ? res[1].docs.map(function (d) { return Object.assign({ _id: d.id }, d.data()); }) : [],
      isPublic: S.profile.arena_public !== false
    };
  };
  D.setArenaPublic = async function (on) {
    await D.userRef().set({ arena_public: !!on }, { merge: true });
    S.profile.arena_public = !!on;
  };
  // Publish an Arena deck as a real site deck, and remember the link so every game updates its record.
  D.publishArenaDeck = async function (deck) {
    var names = Array.from(new Set((deck.cards || []).map(function (c) { return c.name; })));
    var byName = new Map();
    var groups = chunked(names, 70);
    for (var gi = 0; gi < groups.length; gi++) {
      for (var attempt = 1; attempt <= 3; attempt++) {
        try {
          var res = await D.scryfall("https://api.scryfall.com/cards/collection", {
            method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ identifiers: groups[gi].map(function (n) { return { name: n }; }) })
          });
          if (res.ok) { var j = await res.json(); (j.data || []).forEach(function (cd) { if (cd && cd.name) byName.set(cd.name.toLowerCase(), cd); }); break; }
        } catch (e) { /* retry */ }
        if (attempt < 3) await KD.delay(600 * attempt);
      }
      if (gi < groups.length - 1) await KD.delay(120);
    }
    var uid = S.user.id, w = deck.wins || 0, l = deck.losses || 0;
    var deckDoc = {
      name: deck.name || "Arena deck",
      format: (deck.format || "standard").toLowerCase(),
      publisherId: uid,
      displayName: S.user.displayName || "",
      cardCount: (deck.cards || []).reduce(function (s, c) { return s + (c.count || 1); }, 0),
      source: "arena",
      arenaStats: { games: deck.games || 0, wins: w, losses: l, draws: deck.draws || 0, winRate: (w + l) ? Math.round(w / (w + l) * 100) : 0 },
      updatedAt: FV().serverTimestamp(),
      lastModified: FV().serverTimestamp()
    };
    var pubRef;
    if (deck.publishedDeckId) {
      pubRef = db().collection("publicDecks").doc(deck.publishedDeckId);
      await pubRef.set(deckDoc, { merge: true });
      var old = await pubRef.collection("cards").get();
      var gdel = chunked(old.docs, 400);
      for (var i = 0; i < gdel.length; i++) { var bd = db().batch(); gdel[i].forEach(function (x) { bd.delete(x.ref); }); await bd.commit(); }
    } else {
      deckDoc.createdAt = FV().serverTimestamp();
      deckDoc.likes = 0; deckDoc.views = 0;
      pubRef = await db().collection("publicDecks").add(deckDoc);
    }
    var batch = db().batch(), ops = 0, list = deck.cards || [];
    for (var k = 0; k < list.length; k++) {
      var c = list[k];
      var cd = byName.get(String(c.name).toLowerCase());
      var cmd = !!(c.isCommander || c.commander);
      batch.set(pubRef.collection("cards").doc(), cd ? D.cardShape(cd, c.count || 1, cmd) : huskCard(c.name, c.count || 1, cmd));
      if (++ops >= 400) { await batch.commit(); batch = db().batch(); ops = 0; }
    }
    if (ops) await batch.commit();
    await D.userRef().collection("assistantDecks").doc(deck._id).update({ publishedDeckId: pubRef.id });
    deck.publishedDeckId = pubRef.id;
    return pubRef.id;
  };

  // ================================================================ account

  D.purchases = async function () {
    var snap = await D.userRef().collection("assistantPurchases").orderBy("at", "desc").limit(12).get();
    return snap.docs.map(function (d) { return d.data(); });
  };

  // ================================================================ notifications (Realtime Database)

  D.notifications = {
    ref: function () { return firebase.database().ref("notifications").child(S.user.id); },
    listen: function (cb) {
      try {
        this.ref().on("value", function (snap) {
          var list = [];
          snap.forEach(function (ch) { var n = ch.val() || {}; n.key = ch.key; list.push(n); });
          cb(list.sort(function (a, b) { return (b.timestamp || 0) - (a.timestamp || 0); }));
        });
      } catch (e) { console.warn("notifications", e); }
    },
    markRead: function () {
      var ref = this.ref();
      ref.once("value", function (snap) { snap.forEach(function (ch) { if (!(ch.val() || {}).viewed) ref.child(ch.key).update({ viewed: true }); }); });
    },
    clear: function () { return this.ref().remove(); }
  };
})();
